import * as React from 'react';

import { json } from '@codemirror/lang-json';
import { LSPPlugin } from '@codemirror/lsp-client';
import type { Extension } from '@codemirror/state';
import { EditorView } from '@codemirror/view';
import CodeMirror, { ReactCodeMirrorProps } from '@uiw/react-codemirror';

import { FieldError, FormDescription, Label } from '../components/ui/Field';

import { exprCompletion } from './codemirror/exprCompletion';
import {
  expressionCompletion,
  ExpressionVariable,
} from './codemirror/expressionCompletion';
import {
  expressionHighlighting,
  expressionHighlightingTheme,
} from './codemirror/expressionHighlighting';
import { exprLang } from './codemirror/exprLang';
import { uiWebBaseTheme } from './codemirror/theme';
import { cn } from './stylingUtils';

export type CodeEditorProps = {
  /** Current value of the editor */
  value: string;
  /** Callback when the value changes */
  onChange: (value: string) => void;
  /** Language mode for syntax highlighting */
  language?: 'json' | 'expr' | 'text';
  /** Label text displayed above the editor */
  label?: string;
  /** Description text displayed below the label */
  description?: string;
  /** Error message displayed below the editor */
  errorMessage?: string;
  /** Whether the editor is in an invalid state */
  isInvalid?: boolean;
  /** Whether the editor is disabled */
  isDisabled?: boolean;
  /** Whether the editor is read-only */
  isReadOnly?: boolean;
  /** Placeholder text shown when editor is empty */
  placeholder?: string;
  /** Additional CSS classes */
  className?: string;
  /** Height of the editor (CSS value) */
  height?: string;
  /** Minimum height of the editor (CSS value) */
  minHeight?: string;
  /** Available expression variables for auto-completion (used with enableInterpolation) */
  expressionVariables?: ExpressionVariable[];
  /** Enable {{variable}} interpolation syntax for text templates */
  enableInterpolation?: boolean;
  /** Show line numbers gutter */
  showLineNumbers?: boolean;
  /**
   * Additional CodeMirror extensions to include.
   * Use this to pass LSP extensions from useLsp().getExtensions()
   */
  additionalExtensions?: Extension;
  /**
   * Disable the built-in autocompletion (e.g., when using LSP for completions).
   * Default: false (built-in autocompletion is enabled unless expressionVariables are provided)
   */
  disableBuiltinAutocompletion?: boolean;
};

/**
 * CodeEditor component using CodeMirror v6
 *
 * Provides syntax highlighting for JSON and custom {{expression}} syntax,
 * with auto-completion support for expression variables.
 *
 * @example
 * ```tsx
 * <CodeEditor
 *   value={jsonValue}
 *   onChange={setJsonValue}
 *   language="json"
 *   label="Event Payload"
 *   description="JSON payload with {{expression}} support"
 *   expressionVariables={[
 *     { path: 'trigger.data.id', label: 'ID', type: 'string' }
 *   ]}
 * />
 * ```
 */
export const CodeEditor: React.FC<CodeEditorProps> = ({
  value,
  onChange,
  language = 'json',
  label,
  description,
  errorMessage,
  isInvalid,
  isDisabled,
  isReadOnly,
  placeholder,
  className,
  height = '200px',
  minHeight,
  expressionVariables = [],
  enableInterpolation = false,
  showLineNumbers = false,
  additionalExtensions,
  disableBuiltinAutocompletion = false,
}) => {
  // Memoize extensions to prevent unnecessary re-renders
  const extensions = React.useMemo(() => {
    console.log(
      '[LSP-DEBUG] CodeEditor: Building extensions, additionalExtensions:',
      additionalExtensions,
    );
    const exts: ReactCodeMirrorProps['extensions'] = [
      uiWebBaseTheme,
      EditorView.lineWrapping,
    ];

    // Add language support
    if (language === 'json') {
      exts.push(json());
    } else if (language === 'expr') {
      exts.push(exprLang());
    }

    // Add autocompletion for expression variables
    if (expressionVariables.length > 0) {
      if (language === 'expr') {
        // Expr language uses bare variable paths (no {{}} syntax)
        exts.push(exprCompletion(expressionVariables));
      } else if (enableInterpolation) {
        // Other languages with interpolation use {{variable}} syntax
        exts.push(expressionCompletion(expressionVariables));
      }
    }

    // Add {{variable}} highlighting when interpolation is enabled
    if (enableInterpolation) {
      exts.push(expressionHighlighting, expressionHighlightingTheme);
    }

    // Add any additional extensions (e.g., LSP extensions)
    if (additionalExtensions) {
      console.log('[LSP-DEBUG] CodeEditor: Adding additional extensions');
      exts.push(additionalExtensions);
    }

    console.log('[LSP-DEBUG] CodeEditor: Final extensions count:', exts.length);
    return exts;
  }, [
    language,
    enableInterpolation,
    expressionVariables,
    additionalExtensions,
    disableBuiltinAutocompletion,
  ]);

  // Memoize change handler
  const handleChange = React.useCallback(
    (newValue: string) => {
      onChange(newValue);
    },
    [onChange],
  );

  // Debug: log when editor is created and check for LSP plugin
  const handleCreateEditor = React.useCallback((view: EditorView) => {
    console.log('[LSP-DEBUG] CodeEditor: EditorView created');
    // Check if LSP plugin is present using the official API
    // Cast to any to work around version mismatch in @codemirror/view types
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const lspPlugin = LSPPlugin.get(view as any);
    console.log(
      '[LSP-DEBUG] CodeEditor: LSPPlugin.get(view):',
      lspPlugin
        ? {
            uri: lspPlugin.uri,
            client: lspPlugin.client ? 'present' : 'missing',
            clientConnected: lspPlugin.client?.connected,
          }
        : 'null',
    );
  }, []);

  return (
    <div
      className={cn(
        '@CodeEditor',
        'flex flex-col gap-1.5',
        isDisabled && 'opacity-50',
        className,
      )}
    >
      {label && <Label>{label}</Label>}
      {description && <FormDescription>{description}</FormDescription>}

      <div
        className={cn(
          'overflow-hidden rounded-md border transition-shadow',
          'focus-within:border-primary focus-within:ring-[3px] focus-within:ring-primary/20',
          isInvalid && 'border-failure focus-within:ring-failure/20',
          !isInvalid && !isDisabled && 'border-neutral/20',
          isDisabled && 'cursor-not-allowed border-neutral/10 bg-neutral/5',
        )}
      >
        <CodeMirror
          value={value}
          onChange={handleChange}
          extensions={extensions}
          editable={!isDisabled && !isReadOnly}
          readOnly={isReadOnly}
          placeholder={placeholder}
          height={height}
          minHeight={minHeight}
          onCreateEditor={handleCreateEditor}
          basicSetup={{
            lineNumbers: showLineNumbers,
            highlightActiveLineGutter: showLineNumbers,
            highlightActiveLine: false,
            foldGutter: false,
            dropCursor: true,
            allowMultipleSelections: false,
            indentOnInput: true,
            bracketMatching: true,
            closeBrackets: true,
            // Disable built-in autocompletion when:
            // - disableBuiltinAutocompletion is true (e.g., using LSP)
            // - expressionVariables are provided (custom completion)
            autocompletion:
              !disableBuiltinAutocompletion && expressionVariables.length === 0,
            rectangularSelection: false,
            crosshairCursor: false,
            highlightSelectionMatches: true,
            searchKeymap: true,
            tabSize: 2,
          }}
          className="[&_.cm-editor]:outline-none"
        />
      </div>

      {errorMessage && <FieldError>{errorMessage}</FieldError>}
    </div>
  );
};

export type { ExpressionVariable };
